import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Search, Plus, ChevronDown, ChevronUp, Trash2, Edit,
  Package, LayoutGrid, List, X, Check, Loader2,
  Calculator, TrendingUp, TrendingDown, Box, Camera, Upload, FileText, ImageIcon, Sparkles
} from 'lucide-react';
import { toast } from 'sonner';
import { useUserId } from '../context/UserAuthContext';

const API_BASE = typeof window !== 'undefined' ? window.location.origin : (process.env.REACT_APP_BACKEND_URL || '');

// Quote Calculator Component
const QuoteCalculator = () => {
  const [qty, setQty] = useState(100);
  const [unitPrice, setUnitPrice] = useState(150);
  const [discount, setDiscount] = useState(10);
  const TAX_RATE = 0.08;

  const subtotal = qty * unitPrice;
  const discountAmt = subtotal * (discount / 100);
  const taxable = subtotal - discountAmt;
  const tax = taxable * TAX_RATE;
  const total = taxable + tax;

  const fmt = (n) => `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  return (
    <div className="bg-[#0d0d1a] border border-purple-900/40 rounded-xl p-4">
      <div className="flex items-center gap-2 mb-4">
        <Calculator size={16} className="text-purple-400" />
        <h3 className="text-sm font-semibold text-white">Quote Calculator</h3>
      </div>

      {/* Inputs */}
      <div className="space-y-3">
        <div>
          <label className="text-xs text-gray-400 mb-1 block">Quantity</label>
          <input
            type="number"
            value={qty}
            onChange={(e) => setQty(Number(e.target.value))}
            className="w-full bg-[#1a1a2e] border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-purple-500"
            data-testid="quote-qty"
          />
        </div>
        <div>
          <label className="text-xs text-gray-400 mb-1 block">Unit Price ($)</label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
            <input
              type="number"
              value={unitPrice}
              onChange={(e) => setUnitPrice(Number(e.target.value))}
              className="w-full bg-[#1a1a2e] border border-gray-700 rounded-lg pl-7 pr-3 py-2 text-white text-sm focus:outline-none focus:border-purple-500"
              data-testid="quote-unit-price"
            />
          </div>
        </div>
        <div>
          <label className="text-xs text-gray-400 mb-1 block">Discount (%)</label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">%</span>
            <input
              type="number"
              value={discount}
              onChange={(e) => setDiscount(Number(e.target.value))}
              className="w-full bg-[#1a1a2e] border border-gray-700 rounded-lg pl-7 pr-3 py-2 text-white text-sm focus:outline-none focus:border-purple-500"
              data-testid="quote-discount"
            />
          </div>
        </div>
      </div>

      {/* Calculation Results */}
      <div className="mt-4 space-y-1.5 border-t border-gray-800 pt-4">
        <div className="flex justify-between text-sm">
          <span className="text-gray-400">Subtotal:</span>
          <span className="text-white font-medium">{fmt(subtotal)}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-gray-400">Discount:</span>
          <span className="text-green-400 font-medium">-{fmt(discountAmt)}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-gray-400">Tax (8%):</span>
          <span className="text-white font-medium">{fmt(tax)}</span>
        </div>
        <div className="flex justify-between text-base font-bold mt-2 bg-purple-900/30 rounded-lg px-3 py-2">
          <span className="text-white">Total:</span>
          <span className="text-purple-300">{fmt(total)}</span>
        </div>
      </div>

      <button
        className="w-full mt-4 py-2.5 rounded-lg bg-gradient-to-r from-purple-600 to-blue-600 text-white text-sm font-semibold hover:from-purple-700 hover:to-blue-700 transition-all"
        data-testid="generate-quote-btn"
        onClick={() => toast.success(`Quote generated: ${fmt(total)}`)}
      >
        Generate Quote
      </button>
    </div>
  );
};

// Add Item Modal
const AddItemModal = ({ onClose, onAdd, activeTab, initialData = null }) => {
  const [form, setForm] = useState({
    name: initialData?.name || '', 
    category: initialData?.category || '', 
    brand: initialData?.brand || '', 
    size: initialData?.size || '',
    total_quantity: initialData?.total_quantity || 1, 
    available_quantity: initialData?.available_quantity || 1,
    value_per_unit: initialData?.value_per_unit || 0, 
    status: initialData?.status || 'good', 
    tab: initialData?.tab || activeTab,
    image_url: initialData?.image_url || '',
    delivery_date: initialData?.delivery_date || '',
    receipt_url: initialData?.receipt_url || ''
  });
  const [saving, setSaving] = useState(false);
  const [uploadingImg, setUploadingImg] = useState(false);
  const [uploadingReceipt, setUploadingReceipt] = useState(false);
  const imgInputRef = useRef(null);
  const receiptInputRef = useRef(null);

  // Resize + convert to base64 JPEG client-side — no server needed
  const resizeToBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const MAX = 600;
          let w = img.width, h = img.height;
          if (w > MAX || h > MAX) {
            if (w > h) { h = Math.round(h * MAX / w); w = MAX; }
            else { w = Math.round(w * MAX / h); h = MAX; }
          }
          const canvas = document.createElement('canvas');
          canvas.width = w; canvas.height = h;
          canvas.getContext('2d').drawImage(img, 0, 0, w, h);
          resolve(canvas.toDataURL('image/jpeg', 0.78));
        };
        img.onerror = reject;
        img.src = e.target.result;
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  const handleImageFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Please select an image file'); return; }
    setUploadingImg(true);
    try {
      const dataUrl = await resizeToBase64(file);
      setForm(prev => ({ ...prev, image_url: dataUrl }));
    } catch {
      toast.error('Failed to process image');
    } finally {
      setUploadingImg(false);
      if (imgInputRef.current) imgInputRef.current.value = '';
    }
  };

  const handleReceiptFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'application/pdf'];
    if (!validTypes.includes(file.type)) { 
      toast.error('Please select an image or PDF file'); 
      return; 
    }
    setUploadingReceipt(true);
    try {
      // For images, resize; for PDFs, just store the data URL
      if (file.type.startsWith('image/')) {
        const dataUrl = await resizeToBase64(file);
        setForm(prev => ({ ...prev, receipt_url: dataUrl }));
      } else {
        // For PDF, convert to base64
        const reader = new FileReader();
        reader.onload = (e) => {
          setForm(prev => ({ ...prev, receipt_url: e.target.result }));
        };
        reader.readAsDataURL(file);
      }
    } catch {
      toast.error('Failed to process receipt');
    } finally {
      setUploadingReceipt(false);
      if (receiptInputRef.current) receiptInputRef.current.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    setSaving(true);
    try {
      await onAdd(form);
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm">
      <div className="bg-[#0d0d1a] border border-gray-700 rounded-2xl p-6 w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-lg font-bold text-white">{initialData ? 'Edit Item' : 'Add New Item'}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X size={20} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-3">

          {/* Image upload section */}
          <div>
            <label className="text-xs text-gray-400 mb-1 block">Item Image</label>
            <div className="flex gap-3 items-start">
              {/* Preview */}
              <div
                onClick={() => imgInputRef.current?.click()}
                className="w-20 h-20 flex-shrink-0 rounded-xl bg-[#1a1a2e] border-2 border-dashed border-gray-600 hover:border-purple-500 flex items-center justify-center cursor-pointer transition-colors overflow-hidden group"
                data-testid="item-image-preview"
              >
                {uploadingImg ? (
                  <Loader2 size={20} className="animate-spin text-purple-400" />
                ) : form.image_url ? (
                  <img src={form.image_url} alt="preview" className="w-full h-full object-cover" />
                ) : (
                  <Camera size={22} className="text-gray-500 group-hover:text-purple-400 transition-colors" />
                )}
              </div>

              <div className="flex-1 space-y-2">
                {/* Upload button */}
                <button
                  type="button"
                  onClick={() => imgInputRef.current?.click()}
                  className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-purple-600/20 hover:bg-purple-600/30 border border-purple-700/50 rounded-lg text-purple-300 text-xs font-medium transition-colors"
                  data-testid="upload-image-btn"
                >
                  <Camera size={13} />
                  {form.image_url ? 'Change Photo' : 'Upload Photo'}
                </button>

                {/* URL fallback */}
                <input
                  value={form.image_url.startsWith('data:') ? '' : form.image_url}
                  onChange={(e) => setForm(prev => ({ ...prev, image_url: e.target.value }))}
                  placeholder="Or paste image URL..."
                  className="w-full bg-[#1a1a2e] border border-gray-700 rounded-lg px-3 py-1.5 text-white text-xs focus:outline-none focus:border-purple-500 placeholder-gray-600"
                />

                {/* Clear */}
                {form.image_url && (
                  <button
                    type="button"
                    onClick={() => setForm(prev => ({ ...prev, image_url: '' }))}
                    className="text-xs text-gray-500 hover:text-red-400 transition-colors"
                  >
                    Remove image
                  </button>
                )}
              </div>
            </div>
            {/* Hidden file input */}
            <input
              ref={imgInputRef}
              type="file"
              accept="image/*"
              onChange={handleImageFile}
              className="hidden"
              data-testid="item-image-input"
            />
          </div>

          {[
            { label: 'Name *', key: 'name', placeholder: 'e.g. MacBook Pro 16"' },
            { label: 'Category', key: 'category', placeholder: 'e.g. Computers' },
            { label: 'Brand', key: 'brand', placeholder: 'e.g. Apple' },
            { label: 'Size / Variant', key: 'size', placeholder: 'e.g. 16-inch' },
          ].map(({ label, key, placeholder }) => (
            <div key={key}>
              <label className="text-xs text-gray-400 mb-1 block">{label}</label>
              <input
                value={form[key]}
                onChange={(e) => setForm(prev => ({ ...prev, [key]: e.target.value }))}
                placeholder={placeholder}
                className="w-full bg-[#1a1a2e] border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-purple-500"
              />
            </div>
          ))}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-gray-400 mb-1 block">Price ($)</label>
              <input
                type="number"
                value={form.value_per_unit}
                onChange={(e) => setForm(prev => ({ ...prev, value_per_unit: Number(e.target.value) }))}
                className="w-full bg-[#1a1a2e] border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-purple-500"
              />
            </div>
            <div>
              <label className="text-xs text-gray-400 mb-1 block">Stock</label>
              <input
                type="number"
                value={form.total_quantity}
                onChange={(e) => setForm(prev => ({ ...prev, total_quantity: Number(e.target.value), available_quantity: Number(e.target.value) }))}
                className="w-full bg-[#1a1a2e] border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-purple-500"
              />
            </div>
          </div>
          <div>
            <label className="text-xs text-gray-400 mb-1 block">Tab</label>
            <select
              value={form.tab}
              onChange={(e) => setForm(prev => ({ ...prev, tab: e.target.value }))}
              className="w-full bg-[#1a1a2e] border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-purple-500"
            >
              <option value="products">Products</option>
              <option value="equipment">Equipment</option>
            </select>
          </div>

          {/* Delivery Date */}
          <div>
            <label className="text-xs text-gray-400 mb-1 block">Delivery Date</label>
            <input
              type="date"
              value={form.delivery_date}
              onChange={(e) => setForm(prev => ({ ...prev, delivery_date: e.target.value }))}
              className="w-full bg-[#1a1a2e] border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-purple-500"
              data-testid="delivery-date-input"
            />
          </div>

          {/* Receipt Upload */}
          <div>
            <label className="text-xs text-gray-400 mb-1 block">Receipt</label>
            <div className="flex gap-2 items-center">
              <button
                type="button"
                onClick={() => receiptInputRef.current?.click()}
                className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-gray-800 hover:bg-gray-700 border border-gray-600 rounded-lg text-gray-300 text-xs font-medium transition-colors"
                data-testid="upload-receipt-btn"
              >
                {uploadingReceipt ? (
                  <Loader2 size={13} className="animate-spin" />
                ) : (
                  <Upload size={13} />
                )}
                {form.receipt_url ? 'Change Receipt' : 'Upload Receipt'}
              </button>
              {form.receipt_url && (
                <button
                  type="button"
                  onClick={() => setForm(prev => ({ ...prev, receipt_url: '' }))}
                  className="p-2 text-gray-500 hover:text-red-400 transition-colors"
                  title="Remove receipt"
                >
                  <X size={14} />
                </button>
              )}
            </div>
            {form.receipt_url && (
              <p className="text-xs text-green-400 mt-1 flex items-center gap-1">
                <FileText size={12} />
                Receipt attached
              </p>
            )}
            <input
              ref={receiptInputRef}
              type="file"
              accept="image/*,.pdf"
              onChange={handleReceiptFile}
              className="hidden"
              data-testid="receipt-file-input"
            />
          </div>

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 py-2 rounded-lg border border-gray-700 text-gray-300 text-sm hover:bg-gray-800 transition-colors">
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex-1 py-2 rounded-lg bg-purple-600 hover:bg-purple-700 text-white text-sm font-semibold transition-colors flex items-center justify-center gap-2"
              data-testid="save-item-btn"
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
              {initialData ? 'Update Item' : 'Save Item'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Batch Upload Modal - supports CSV, Excel, PDF, and AI image scanning
const BatchUploadModal = ({ onClose, onUpload, activeTab }) => {
  const [file, setFile] = useState(null);
  const [parsing, setParsing] = useState(false);
  const [parsedItems, setParsedItems] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadMode, setUploadMode] = useState('file'); // 'file' | 'image'
  const [imagePreview, setImagePreview] = useState(null);
  const fileInputRef = useRef(null);
  const imageInputRef = useRef(null);

  const IMAGE_EXTS = ['jpg', 'jpeg', 'png', 'webp', 'heic'];

  const handleFileSelect = async (e) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    const ext = selectedFile.name.split('.').pop()?.toLowerCase();
    const validExts = ['csv', 'xlsx', 'xls', 'pdf'];

    if (!validExts.includes(ext)) {
      toast.error('Please upload a CSV, Excel (.xlsx/.xls), or PDF file');
      return;
    }

    setFile(selectedFile);
    setParsing(true);
    setParsedItems([]);

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('tab', activeTab);

      const res = await fetch(`${API_BASE}/api/inventory/parse-file`, {
        method: 'POST',
        body: formData
      });
      const data = await res.json();

      if (data.items && data.items.length > 0) {
        setParsedItems(data.items);
        toast.success(`Found ${data.items.length} items`);
      } else {
        toast.error(data.error || 'No items found. Check columns: Name, Category, Brand, Price, Quantity');
      }
    } catch (err) {
      toast.error('Failed to parse file');
    } finally {
      setParsing(false);
    }
  };

  const handleImageSelect = async (e) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    const ext = selectedFile.name.split('.').pop()?.toLowerCase();
    if (!IMAGE_EXTS.includes(ext)) {
      toast.error('Please upload a JPG, PNG, WEBP, or HEIC image');
      return;
    }

    setFile(selectedFile);
    setImagePreview(URL.createObjectURL(selectedFile));
    setParsing(true);
    setParsedItems([]);

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('tab', activeTab);

      const res = await fetch(`${API_BASE}/api/inventory/parse-image`, {
        method: 'POST',
        body: formData
      });
      const data = await res.json();

      if (data.items && data.items.length > 0) {
        setParsedItems(data.items);
        toast.success(`AI found ${data.items.length} item${data.items.length > 1 ? 's' : ''} in image`);
      } else {
        toast.error(data.error || 'No items found in image. Try a clearer photo.');
      }
    } catch (err) {
      toast.error('Failed to scan image');
    } finally {
      setParsing(false);
    }
  };

  const switchMode = (mode) => {
    setUploadMode(mode);
    setFile(null);
    setParsedItems([]);
    setImagePreview(null);
  };

  const handleUpload = async () => {
    if (parsedItems.length === 0) return;
    setUploading(true);
    try {
      const result = await onUpload(parsedItems);
      if (result.success) {
        toast.success(`Successfully added ${result.count} items!`);
        onClose();
      }
    } catch (err) {
      toast.error('Failed to upload items');
    } finally {
      setUploading(false);
    }
  };

  const removeItem = (index) => {
    setParsedItems(prev => prev.filter((_, i) => i !== index));
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm">
      <div className="bg-[#0d0d1a] border border-gray-700 rounded-2xl p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-lg font-bold text-white flex items-center gap-2">
            <Upload size={20} className="text-purple-400" />
            Batch Upload {activeTab === 'products' ? 'Products' : 'Equipment'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white" data-testid="batch-modal-close">
            <X size={20} />
          </button>
        </div>

        {/* Mode Toggle */}
        <div className="flex gap-2 mb-5 p-1 bg-gray-900/60 rounded-xl">
          <button
            onClick={() => switchMode('file')}
            className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-lg text-sm font-medium transition-all ${
              uploadMode === 'file'
                ? 'bg-purple-600 text-white shadow'
                : 'text-gray-400 hover:text-white'
            }`}
            data-testid="mode-file-btn"
          >
            <FileText size={15} />
            CSV / Excel / PDF
          </button>
          <button
            onClick={() => switchMode('image')}
            className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-lg text-sm font-medium transition-all ${
              uploadMode === 'image'
                ? 'bg-purple-600 text-white shadow'
                : 'text-gray-400 hover:text-white'
            }`}
            data-testid="mode-image-btn"
          >
            <Sparkles size={15} />
            AI Image Scan
          </button>
        </div>

        {/* FILE MODE */}
        {uploadMode === 'file' && (
          <>
            <div
              onClick={() => fileInputRef.current?.click()}
              className="border-2 border-dashed border-gray-600 hover:border-purple-500 rounded-xl p-8 text-center cursor-pointer transition-colors mb-4"
              data-testid="file-dropzone"
            >
              {parsing ? (
                <div className="flex flex-col items-center">
                  <Loader2 size={32} className="animate-spin text-purple-400 mb-2" />
                  <p className="text-gray-400">Parsing file...</p>
                </div>
              ) : file ? (
                <div className="flex flex-col items-center">
                  <FileText size={32} className="text-purple-400 mb-2" />
                  <p className="text-white font-medium">{file.name}</p>
                  <p className="text-gray-500 text-sm mt-1">Click to choose a different file</p>
                </div>
              ) : (
                <div className="flex flex-col items-center">
                  <Upload size={32} className="text-gray-500 mb-2" />
                  <p className="text-gray-300 font-medium">Drop your file here or click to browse</p>
                  <p className="text-gray-500 text-sm mt-1">Supports CSV, Excel (.xlsx, .xls), PDF</p>
                </div>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,.xlsx,.xls,.pdf"
                onChange={handleFileSelect}
                className="hidden"
                data-testid="batch-file-input"
              />
            </div>
            <div className="bg-gray-900/50 rounded-lg p-3 mb-4 text-xs">
              <p className="text-gray-400 font-medium mb-1">Expected columns:</p>
              <p className="text-gray-500">Name, Category, Brand, Size, Price (or Value), Quantity (or Stock), Status</p>
            </div>
          </>
        )}

        {/* IMAGE MODE */}
        {uploadMode === 'image' && (
          <>
            <div
              onClick={() => imageInputRef.current?.click()}
              className="border-2 border-dashed border-gray-600 hover:border-purple-500 rounded-xl p-6 text-center cursor-pointer transition-colors mb-4 relative overflow-hidden"
              data-testid="image-dropzone"
            >
              {parsing ? (
                <div className="flex flex-col items-center py-4">
                  <div className="relative mb-3">
                    <Sparkles size={32} className="text-purple-400 animate-pulse" />
                  </div>
                  <p className="text-purple-300 font-medium">AI is scanning your image...</p>
                  <p className="text-gray-500 text-xs mt-1">Identifying products and extracting details</p>
                </div>
              ) : imagePreview ? (
                <div className="flex flex-col items-center">
                  <img
                    src={imagePreview}
                    alt="Preview"
                    className="max-h-40 max-w-full object-contain rounded-lg mb-2"
                  />
                  <p className="text-white text-sm font-medium">{file?.name}</p>
                  <p className="text-gray-500 text-xs mt-1">Click to choose a different image</p>
                </div>
              ) : (
                <div className="flex flex-col items-center py-4">
                  <div className="w-16 h-16 rounded-full bg-purple-500/10 flex items-center justify-center mb-3">
                    <ImageIcon size={28} className="text-purple-400" />
                  </div>
                  <p className="text-gray-300 font-medium">Upload a photo to scan with AI</p>
                  <p className="text-gray-500 text-sm mt-1">Product shelf · Receipt · Price tag · Inventory list</p>
                  <p className="text-gray-600 text-xs mt-2">JPG, PNG, WEBP, HEIC</p>
                </div>
              )}
              <input
                ref={imageInputRef}
                type="file"
                accept=".jpg,.jpeg,.png,.webp,.heic,image/jpeg,image/png,image/webp"
                onChange={handleImageSelect}
                className="hidden"
                data-testid="batch-image-input"
              />
            </div>
            <div className="bg-purple-900/20 border border-purple-800/30 rounded-lg p-3 mb-4 text-xs flex items-start gap-2">
              <Sparkles size={13} className="text-purple-400 mt-0.5 shrink-0" />
              <p className="text-purple-300">AI will automatically detect product names, categories, brands, prices, and quantities from your photo.</p>
            </div>
          </>
        )}

        {/* Parsed Items Preview */}
        {parsedItems.length > 0 && (
          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-semibold text-white flex items-center gap-1.5">
                {uploadMode === 'image' && <Sparkles size={13} className="text-purple-400" />}
                {parsedItems.length} Items Found
              </h3>
              <button onClick={() => setParsedItems([])} className="text-xs text-gray-500 hover:text-red-400">
                Clear All
              </button>
            </div>
            <div className="max-h-64 overflow-y-auto space-y-2 border border-gray-800 rounded-lg p-2">
              {parsedItems.map((item, idx) => (
                <div key={idx} className="flex items-center justify-between bg-gray-900/50 rounded-lg px-3 py-2">
                  <div className="flex-1 min-w-0">
                    <p className="text-white text-sm font-medium truncate">{item.name}</p>
                    <p className="text-gray-500 text-xs">
                      {item.category && `${item.category}`}
                      {item.brand && ` · ${item.brand}`}
                      {item.size && ` · ${item.size}`}
                      {` · $${item.value_per_unit || 0}`}
                      {` · Qty: ${item.total_quantity || 1}`}
                    </p>
                  </div>
                  <button onClick={() => removeItem(idx)} className="p-1 text-gray-500 hover:text-red-400 ml-2">
                    <X size={14} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 py-2.5 rounded-lg border border-gray-700 text-gray-300 text-sm hover:bg-gray-800 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleUpload}
            disabled={parsedItems.length === 0 || uploading}
            className="flex-1 py-2.5 rounded-lg bg-purple-600 hover:bg-purple-700 disabled:bg-gray-700 disabled:text-gray-500 text-white text-sm font-semibold transition-colors flex items-center justify-center gap-2"
            data-testid="batch-upload-btn"
          >
            {uploading ? (
              <><Loader2 size={14} className="animate-spin" />Uploading...</>
            ) : (
              <><Upload size={14} />Upload {parsedItems.length} Item{parsedItems.length !== 1 ? 's' : ''}</>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

// Inventory Row
const InventoryRow = ({ item, selected, onSelect, onDelete, onEdit, onReceiptPreview }) => {
  const [expanded, setExpanded] = useState(false);
  const [imgError, setImgError] = useState(false);

  const initials = item.name?.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
  
  // Format delivery date
  const formatDeliveryDate = (dateStr) => {
    if (!dateStr) return '—';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    } catch {
      return dateStr;
    }
  };

  return (
    <>
      <tr
        className={`border-b border-gray-800/50 hover:bg-gray-900/30 transition-colors ${selected ? 'bg-gray-900/20' : ''}`}
        data-testid={`inventory-row-${item.id}`}
      >
        <td className="pl-4 pr-2 py-3">
          <input
            type="checkbox"
            checked={selected}
            onChange={() => onSelect(item.id)}
            className="w-4 h-4 rounded border-gray-600 accent-purple-500"
          />
        </td>
        <td className="px-3 py-3">
          <div className="w-9 h-9 rounded-lg overflow-hidden bg-gray-800 flex items-center justify-center text-xs font-bold text-gray-400">
            {item.image_url && !imgError ? (
              <img
                src={item.image_url}
                alt={item.name}
                className="w-full h-full object-cover"
                onError={() => setImgError(true)}
              />
            ) : initials}
          </div>
        </td>
        <td className="px-3 py-3 text-gray-400 text-sm font-mono">{item.display_id || item.id?.slice(0, 8)}</td>
        <td className="px-3 py-3 text-white text-sm font-medium">{item.name}</td>
        <td className="px-3 py-3 text-gray-400 text-sm">{item.size || '—'}</td>
        <td className="px-3 py-3 text-white text-sm font-semibold">
          ${item.value_per_unit?.toLocaleString('en-US', { minimumFractionDigits: 2 })}
        </td>
        <td className="px-3 py-3">
          <span className={`text-sm font-semibold ${item.available_quantity < 30 ? 'text-red-400' : 'text-white'}`}>
            {item.available_quantity}
          </span>
        </td>
        <td className="px-3 py-3 text-gray-400 text-sm">
          {formatDeliveryDate(item.delivery_date)}
        </td>
        <td className="px-3 py-3">
          {item.receipt_url ? (
            <button
              onClick={() => onReceiptPreview?.({ url: item.receipt_url, name: item.name })}
              className="p-1.5 rounded-lg bg-green-500/20 text-green-400 hover:bg-green-500/30 transition-colors"
              title="View Receipt"
              data-testid={`view-receipt-${item.id}`}
            >
              <FileText size={14} />
            </button>
          ) : (
            <span className="text-gray-600 text-sm">—</span>
          )}
        </td>
        <td className="px-3 py-3">
          <button
            onClick={() => setExpanded(!expanded)}
            className="text-gray-400 hover:text-white transition-colors"
            data-testid={`expand-row-${item.id}`}
          >
            {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>
        </td>
      </tr>
      {expanded && (
        <tr className="bg-gray-900/20 border-b border-gray-800/50">
          <td colSpan={10} className="px-6 py-4">
            <div className="flex items-start gap-6">
              <div className="w-20 h-20 rounded-xl overflow-hidden bg-gray-800 flex items-center justify-center text-xl font-bold text-gray-400">
                {item.image_url && !imgError ? (
                  <img src={item.image_url} alt={item.name} className="w-full h-full object-cover" />
                ) : initials}
              </div>
              <div className="flex-1 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div><span className="text-gray-500 block text-xs mb-0.5">Brand</span><span className="text-white">{item.brand || '—'}</span></div>
                <div><span className="text-gray-500 block text-xs mb-0.5">Category</span><span className="text-white">{item.category || '—'}</span></div>
                <div><span className="text-gray-500 block text-xs mb-0.5">Status</span><span className={`capitalize ${item.status === 'good' ? 'text-green-400' : 'text-yellow-400'}`}>{item.status}</span></div>
                <div><span className="text-gray-500 block text-xs mb-0.5">Total Stock</span><span className="text-white">{item.total_quantity}</span></div>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => onEdit(item)}
                  className="p-2 rounded-lg border border-gray-700 text-gray-400 hover:text-white hover:border-gray-500 transition-colors"
                  data-testid={`edit-item-${item.id}`}
                >
                  <Edit size={14} />
                </button>
                <button
                  onClick={() => onDelete(item.id)}
                  className="p-2 rounded-lg border border-red-900/50 text-red-400 hover:bg-red-900/20 transition-colors"
                  data-testid={`delete-item-${item.id}`}
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
};

export default function Inventory() {
  const userId = useUserId();
  const [activeTab, setActiveTab] = useState('equipment');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [brandFilter, setBrandFilter] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [showAddModal, setShowAddModal] = useState(false);
  const [showBatchModal, setShowBatchModal] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [stats, setStats] = useState({ total_items: 0, available: 0, in_use: 0, total_value: 0 });
  const [viewMode, setViewMode] = useState('table');
  const [receiptPreview, setReceiptPreview] = useState(null); // { url, name };

  const fetchItems = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ user_id: userId, tab: activeTab });
      if (categoryFilter !== 'all') params.append('category', categoryFilter);
      if (brandFilter !== 'all') params.append('brand', brandFilter);
      if (search) params.append('search', search);
      if (sortBy !== 'newest') params.append('sort_by', sortBy);

      const res = await fetch(`${API_BASE}/api/inventory/items?${params}`);
      const data = await res.json();
      setItems(data.items || []);
    } catch (err) {
      console.error('Failed to fetch items', err);
    } finally {
      setLoading(false);
    }
  }, [userId, activeTab, categoryFilter, brandFilter, search, sortBy]);

  const fetchStats = useCallback(async () => {
    if (!userId) return;
    try {
      const res = await fetch(`${API_BASE}/api/inventory/stats?user_id=${userId}`);
      const data = await res.json();
      setStats(data);
    } catch { /* ignored */ }
  }, [userId]);

  // Seed then fetch on mount
  useEffect(() => {
    const init = async () => {
      if (!userId) return;
      try {
        const res = await fetch(`${API_BASE}/api/inventory/items?user_id=${userId}`);
        const data = await res.json();
        if (!data.items || data.items.length === 0) {
          await fetch(`${API_BASE}/api/inventory/seed?user_id=${userId}`, { method: 'POST' });
        }
      } catch { /* ignored */ }
      fetchItems();
      fetchStats();
    };
    init();
  }, [userId]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const handleAdd = async (form) => {
    try {
      const res = await fetch(`${API_BASE}/api/inventory/items?user_id=${userId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      });
      const data = await res.json();
      if (data.item) {
        setItems(prev => [data.item, ...prev]);
        fetchStats();
        toast.success('Item added!');
      }
    } catch { toast.error('Failed to add item'); }
  };

  const handleBatchUpload = async (parsedItems) => {
    try {
      const res = await fetch(`${API_BASE}/api/inventory/items/batch?user_id=${userId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: parsedItems.map(item => ({ ...item, tab: activeTab })) })
      });
      const data = await res.json();
      if (data.items && data.items.length > 0) {
        setItems(prev => [...data.items, ...prev]);
        fetchStats();
        return { success: true, count: data.items.length };
      }
      return { success: false };
    } catch (err) {
      console.error('Batch upload error:', err);
      return { success: false };
    }
  };

  const handleDelete = async (itemId) => {
    if (!window.confirm('Delete this item?')) return;
    try {
      await fetch(`${API_BASE}/api/inventory/items/${itemId}?user_id=${userId}`, { method: 'DELETE' });
      setItems(prev => prev.filter(i => i.id !== itemId));
      fetchStats();
      toast.success('Item deleted');
    } catch { toast.error('Failed to delete'); }
  };

  const handleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const allSelected = items.length > 0 && selectedIds.size === items.length;
  const toggleSelectAll = () => {
    setSelectedIds(allSelected ? new Set() : new Set(items.map(i => i.id)));
  };

  // Derived filter options from current items
  const allItems = items;
  const categories = ['all', ...new Set(allItems.map(i => i.category).filter(Boolean))];
  const brands = ['all', ...new Set(allItems.map(i => i.brand).filter(Boolean))];

  return (
    <div className="min-h-screen bg-black text-white flex flex-col">
      {/* Header */}
      <div className="px-6 pt-6 pb-4 border-b border-gray-800">
        <h1 className="text-3xl font-bold text-white mb-4">Inventory</h1>
        {/* Tabs */}
        <div className="flex gap-2">
          {['products', 'equipment'].map(tab => (
            <button
              key={tab}
              onClick={() => { setActiveTab(tab); setSelectedIds(new Set()); }}
              className={`px-5 py-2 rounded-lg text-sm font-semibold capitalize transition-colors ${
                activeTab === tab
                  ? 'bg-purple-600 text-white'
                  : 'text-gray-400 hover:text-white hover:bg-gray-800'
              }`}
              data-testid={`tab-${tab}`}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>
      </div>

      <div className="flex flex-1 overflow-hidden">
        {/* Left Panel */}
        <div className="w-72 flex-shrink-0 border-r border-gray-800 p-4 overflow-y-auto space-y-4">
          {/* Quote Calculator */}
          <QuoteCalculator />

          {/* Stats */}
          <div className="bg-[#0d0d1a] border border-gray-800 rounded-xl p-4">
            <h3 className="text-xs text-gray-400 font-semibold uppercase tracking-wider mb-3">Overview</h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm text-gray-400">
                  <Box size={14} className="text-purple-400" />
                  Total Items
                </div>
                <span className="text-white font-bold text-lg">{stats.total_items.toLocaleString()}</span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm text-gray-400">
                  <TrendingUp size={14} className="text-green-400" />
                  Available
                </div>
                <span className="text-green-400 font-bold text-lg">{stats.available.toLocaleString()}</span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm text-gray-400">
                  <TrendingDown size={14} className="text-blue-400" />
                  In Use
                </div>
                <span className="text-blue-400 font-bold text-lg">{stats.in_use.toLocaleString()}</span>
              </div>
              <div className="border-t border-gray-800 pt-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-500">Total Value</span>
                  <span className="text-white font-semibold text-sm">
                    ${stats.total_value?.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Toolbar */}
          <div className="px-4 py-3 border-b border-gray-800 flex flex-wrap items-center gap-3">
            {/* Search */}
            <div className="relative flex-1 min-w-48">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Type id number or name of items"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full bg-gray-900 border border-gray-700 rounded-lg pl-9 pr-4 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-purple-500"
                data-testid="inventory-search"
              />
            </div>

            {/* Category Filter */}
            <div className="relative">
              <select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                className="bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-sm text-gray-300 focus:outline-none focus:border-purple-500 appearance-none pr-8"
                data-testid="category-filter"
              >
                {categories.map(c => (
                  <option key={c} value={c}>
                    {c === 'all' ? 'All Categories' : c}
                  </option>
                ))}
              </select>
              <ChevronDown size={12} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
            </div>

            {/* Brand Filter */}
            <div className="relative">
              <select
                value={brandFilter}
                onChange={(e) => setBrandFilter(e.target.value)}
                className="bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-sm text-gray-300 focus:outline-none focus:border-purple-500 appearance-none pr-8"
                data-testid="brand-filter"
              >
                {brands.map(b => (
                  <option key={b} value={b}>
                    {b === 'all' ? 'All Brands' : b}
                  </option>
                ))}
              </select>
              <ChevronDown size={12} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
            </div>

            {/* Sort */}
            <div className="relative">
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-sm text-gray-300 focus:outline-none focus:border-purple-500 appearance-none pr-8"
                data-testid="sort-filter"
              >
                <option value="newest">Sort: Newest</option>
                <option value="price_high">Price: High → Low</option>
                <option value="price_low">Price: Low → High</option>
                <option value="stock_high">Most Stock</option>
                <option value="name_az">Name A → Z</option>
              </select>
              <ChevronDown size={12} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
            </div>

            {/* View Toggle */}
            <div className="flex border border-gray-700 rounded-lg overflow-hidden">
              <button
                onClick={() => setViewMode('table')}
                className={`p-2 ${viewMode === 'table' ? 'bg-purple-600 text-white' : 'text-gray-400 hover:bg-gray-800'}`}
              >
                <List size={14} />
              </button>
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 ${viewMode === 'grid' ? 'bg-purple-600 text-white' : 'text-gray-400 hover:bg-gray-800'}`}
              >
                <LayoutGrid size={14} />
              </button>
            </div>

            {/* Add Items */}
            <button
              onClick={() => setShowAddModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm font-semibold transition-colors"
              data-testid="add-items-btn"
            >
              <Plus size={14} />
              Add Item
            </button>

            {/* Batch Upload */}
            <button
              onClick={() => setShowBatchModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-gray-800 hover:bg-gray-700 border border-gray-600 text-white rounded-lg text-sm font-semibold transition-colors"
              data-testid="batch-upload-btn"
            >
              <Upload size={14} />
              Upload File
            </button>
          </div>

          {/* Table / Grid */}
          <div className="flex-1 overflow-auto">
            {loading ? (
              <div className="flex items-center justify-center h-48">
                <Loader2 className="animate-spin text-purple-400 w-8 h-8" />
              </div>
            ) : items.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-48 text-gray-500">
                <Package size={40} className="mb-3 opacity-30" />
                <p className="text-sm">No items found</p>
                <button
                  onClick={() => setShowAddModal(true)}
                  className="mt-3 px-4 py-2 bg-purple-600 text-white rounded-lg text-sm hover:bg-purple-700"
                >
                  Add First Item
                </button>
              </div>
            ) : viewMode === 'table' ? (
              <table className="w-full" data-testid="inventory-table">
                <thead>
                  <tr className="border-b border-gray-800 bg-black sticky top-0 z-10">
                    <th className="pl-4 pr-2 py-3 text-left">
                      <input
                        type="checkbox"
                        checked={allSelected}
                        onChange={toggleSelectAll}
                        className="w-4 h-4 rounded border-gray-600 accent-purple-500"
                      />
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider w-12"></th>
                    {['ID NUMBER', 'NAME', 'SIZE', 'PRICE', 'STOCK', 'DELIVERY', 'RECEIPT', ''].map((h, i) => (
                      <th key={i} className="px-3 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {items.map(item => (
                    <InventoryRow
                      key={item.id}
                      item={item}
                      selected={selectedIds.has(item.id)}
                      onSelect={handleSelect}
                      onDelete={handleDelete}
                      onEdit={setEditItem}
                      onReceiptPreview={setReceiptPreview}
                    />
                  ))}
                </tbody>
              </table>
            ) : (
              /* Grid View */
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 p-4">
                {items.map(item => {
                  const initials = item.name?.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
                  return (
                    <div
                      key={item.id}
                      className="bg-[#0d0d1a] border border-gray-800 rounded-xl p-4 hover:border-purple-700/50 transition-colors"
                      data-testid={`grid-item-${item.id}`}
                    >
                      <div className="w-full aspect-square rounded-lg overflow-hidden bg-gray-800 flex items-center justify-center text-xl font-bold text-gray-400 mb-3">
                        {item.image_url ? (
                          <img src={item.image_url} alt={item.name} className="w-full h-full object-cover" />
                        ) : initials}
                      </div>
                      <h3 className="text-white text-sm font-semibold line-clamp-2 mb-1">{item.name}</h3>
                      <p className="text-gray-500 text-xs mb-2">{item.brand || item.category}</p>
                      <div className="flex justify-between items-center">
                        <span className="text-white font-bold text-sm">
                          ${item.value_per_unit?.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                        </span>
                        <span className="text-xs text-gray-500">Stock: {item.available_quantity}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Bottom bar when items selected */}
          {selectedIds.size > 0 && (
            <div className="px-4 py-3 border-t border-gray-800 bg-gray-900/50 flex items-center justify-between">
              <span className="text-sm text-gray-400">{selectedIds.size} item{selectedIds.size !== 1 ? 's' : ''} selected</span>
              <button
                onClick={() => {
                  if (window.confirm(`Delete ${selectedIds.size} items?`)) {
                    selectedIds.forEach(id => handleDelete(id));
                    setSelectedIds(new Set());
                  }
                }}
                className="flex items-center gap-2 px-3 py-1.5 bg-red-900/30 border border-red-900/50 text-red-400 rounded-lg text-sm hover:bg-red-900/40 transition-colors"
              >
                <Trash2 size={14} />
                Delete Selected
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Add Modal */}
      {showAddModal && (
        <AddItemModal
          onClose={() => setShowAddModal(false)}
          onAdd={handleAdd}
          activeTab={activeTab}
        />
      )}

      {/* Batch Upload Modal */}
      {showBatchModal && (
        <BatchUploadModal
          onClose={() => setShowBatchModal(false)}
          onUpload={handleBatchUpload}
          activeTab={activeTab}
        />
      )}

      {/* Edit Modal (reuses Add with prefilled data) */}
      {editItem && (
        <AddItemModal
          onClose={() => setEditItem(null)}
          onAdd={async (form) => {
            try {
              const res = await fetch(
                `${API_BASE}/api/inventory/items/${editItem.id}?user_id=${userId}`,
                {
                  method: 'PUT',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify(form)
                }
              );
              const data = await res.json();
              if (data.item) {
                setItems(prev => prev.map(i => i.id === editItem.id ? data.item : i));
                fetchStats();
                toast.success('Item updated!');
              }
              setEditItem(null);
            } catch { toast.error('Failed to update item'); }
          }}
          activeTab={activeTab}
          initialData={editItem}
        />
      )}

      {/* Receipt Preview Modal */}
      {receiptPreview && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm"
          onClick={() => setReceiptPreview(null)}
        >
          <div 
            className="bg-[#0d0d1a] border border-gray-700 rounded-2xl p-4 max-w-3xl max-h-[90vh] overflow-auto mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-white flex items-center gap-2">
                <FileText size={18} className="text-purple-400" />
                Receipt: {receiptPreview.name}
              </h3>
              <button 
                onClick={() => setReceiptPreview(null)}
                className="text-gray-400 hover:text-white"
                data-testid="close-receipt-preview"
              >
                <X size={20} />
              </button>
            </div>
            {receiptPreview.url?.startsWith('data:application/pdf') ? (
              <div className="text-center py-8">
                <FileText size={48} className="mx-auto text-gray-500 mb-4" />
                <p className="text-gray-400 mb-4">PDF Receipt</p>
                <a 
                  href={receiptPreview.url} 
                  download={`receipt-${receiptPreview.name}.pdf`}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm font-semibold"
                >
                  <Upload size={14} className="rotate-180" />
                  Download PDF
                </a>
              </div>
            ) : (
              <img 
                src={receiptPreview.url} 
                alt={`Receipt for ${receiptPreview.name}`}
                className="max-w-full max-h-[70vh] object-contain rounded-lg mx-auto"
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
}
